import { createElement, Fragment } from 'react'
import { toast } from 'react-toastify'
import { isMatching, P } from 'ts-pattern'
import { getPricingHeaders } from 'src/components/GenerationTask/Common/Pricing'
import { UIDialog } from 'src/components/ui/Modal'
import Text from 'src/components/ui/Text'
import type {
  CreateGenerationTaskMutation,
  Task,
} from 'src/services/apollo/generated/graphql'
import { taskHasFavImages } from 'src/utils/task'
import {
  executeCreateGenerationTask,
  executeDeleteGenerationTask,
  executeUpdateGenerationTask,
  fetchGetTaskById,
} from '../apollo/task'

type DeleteOperationOpts = {
  /** Skip confirmation (like `--quiet`) */
  skipConfirm?: boolean
}

/**
 * Delete a task
 *
 * @returns The operation result
 *   - `true`: The operation was successful
 *   - `false`: Error occurred during the operation
 *   - `undefined`: The operation was cancelled
 */
export async function deleteTask(
  /**
   * The task or task ID to delete. When passing a task ID, the task will be
   * fetched first.
   */
  taskOrTaskId: string | Task,
  opts?: DeleteOperationOpts,
) {
  const task = await resolveTask(taskOrTaskId)
  if (!task) {
    toast.error(
      createElement(Text, {
        i18nKey: 'artwork:delete-task.notice.resolve-task-failed',
      }),
    )
    return false
  }

  // Check if user has favorite images in the task.
  // Should notify user if this is the case.
  const havingFavMedia = taskHasFavImages(task)

  const toProceed =
    opts?.skipConfirm ||
    (await UIDialog.confirm({
      heading: createElement(Text, {
        i18nKey: 'artwork:delete-task.confirm-dialog.title',
      }),
      content: createElement(Fragment, null, [
        createElement(
          'p',
          null,
          createElement(Text, {
            i18nKey: 'artwork:delete-task.confirm-dialog.description.content',
          }),
        ),
        havingFavMedia
          ? createElement(
              'p',
              { className: 'text-red-600 dark:text-red-500' },
              createElement(Text, {
                i18nKey:
                  'artwork:delete-task.confirm-dialog.description.fav-del-reminder',
              }),
            )
          : null,
      ]),
    }))

  if (toProceed) {
    try {
      await executeDeleteGenerationTask({ taskId: task.id })
      toast.success(
        createElement(Text, { i18nKey: 'artwork:delete-task.notice.success' }),
      )
      return true
    } catch (err) {
      toast.error(
        createElement('p', null, [
          createElement(Text, {
            i18nKey: 'artwork:delete-task.notice.fail',
          }),
          ` (${String(err)})`,
        ]),
      )
    }
    return false
  }
}

/**
 * Delete a task media
 *
 * @returns The operation result
 *   - `true`: The operation was successful
 *   - `false`: Error occurred during the operation
 *   - `undefined`: The operation was cancelled
 */
export async function deleteTaskMedia(
  /**
   * The task or task ID to delete from. When passing a task ID, the task will
   * be fetched first.
   */
  taskOrTaskId: string | Task,
  mediaId: string,
  opts?:
    | DeleteOperationOpts
    | ((shouldDeleteTask: boolean) => DeleteOperationOpts | void),
) {
  const task = await resolveTask(taskOrTaskId)
  if (!task) {
    toast.error(
      createElement(Text, {
        i18nKey: 'artwork:delete-task.notice.resolve-task-failed',
      }),
    )
    return false
  }

  const shouldDeleteTask = isMatching(
    P.union(
      // - If the task is a batch task. AND
      // - Every task media is already deleted OR to be deleted. AND
      // - The media to be deleted is one of the task medias.
      {
        outputs: {
          batch: P.intersection(
            P.array(P.union({ mediaId }, { deletedAt: P.nonNullable })),
            P.when(batch =>
              // `batch` must be an array of object here
              (batch as Array<{ mediaId: string }>).some(
                it => it.mediaId === mediaId,
              ),
            ),
          ),
        },
      },
      // - If the task is a non-batch task. AND
      // - The task media is the one to be deleted.
      {
        outputs: P.optional({ batch: P.optional(P.nullish) }),
        media: { id: mediaId },
      },
    ),
    task,
  )
  const resolvedOpts =
    typeof opts === 'function' ? opts(shouldDeleteTask) : opts
  if (shouldDeleteTask) {
    return deleteTask(task, resolvedOpts || undefined)
  }

  const toProceed =
    resolvedOpts?.skipConfirm ||
    (await UIDialog.confirm({
      heading: createElement(Text, {
        i18nKey: 'artwork:delete-media.confirm-dialog.title',
      }),
      content: createElement(Text, {
        i18nKey: 'artwork:delete-media.confirm-dialog.description.content',
      }),
    }))

  if (toProceed) {
    try {
      await executeUpdateGenerationTask({
        id: task.id,
        input: {
          deleteBatchMedia: {
            mediaId,
          },
        },
      })
      toast.success(
        createElement(Text, { i18nKey: 'artwork:delete-media.notice.success' }),
      )
      return true
    } catch (err) {
      toast.error(
        createElement('p', null, [
          createElement(Text, { i18nKey: 'artwork:delete-media.notice.fail' }, [
            ` (${String(err)})`,
          ]),
        ]),
      )
      return false
    }
  }
}

async function resolveTask(
  taskOrTaskId: string | Task,
): Promise<Task | undefined> {
  return typeof taskOrTaskId === 'string'
    ? (await fetchGetTaskById({ id: taskOrTaskId })).data?.task
    : taskOrTaskId
}

export async function createGenerationTaskWithTurnstileToken(
  parameters: any,
  turnstileToken: string | null,
  jslibVersion?: string,
): Promise<CreateGenerationTaskMutation | null> {
  const headers: Record<string, string> = {}
  if (turnstileToken) {
    headers['X-Turnstile-Token'] = turnstileToken
  }
  return (
    (
      await executeCreateGenerationTask(
        { parameters },
        {
          context: {
            headers: {
              ...getPricingHeaders(jslibVersion),
              ...headers,
            },
          },
        },
      )
    ).data ?? null
  )
}
